import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { authApi } from '../../api/authApi';
import { ME_QUERY_KEY } from '../../queries/useMe';
import { useAuthStore } from '../../store/useAuthStore';

interface OAuthPostMessage {
  type: 'oauth_success' | 'oauth_error';
  code: string;
}

export function GoogleSignInButton(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const closeModal = useAuthStore((s) => s.closeAuthModal);
  const [error, setError] = useState<string | null>(null);

  const start = (): void => {
    setError(null);
    const popup = window.open(
      authApi.googleRedirectUrl(),
      'oauth_google_popup',
      'width=500,height=620,scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no,location=no',
    );
    if (!popup) {
      // Popup blocked → fallback to top-level redirect
      window.location.href = authApi.googleRedirectUrl();
      return;
    }

    const onMessage = (e: MessageEvent<OAuthPostMessage>): void => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || (e.data.type !== 'oauth_success' && e.data.type !== 'oauth_error')) return;
      cleanup();
      if (e.data.type === 'oauth_success') {
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        closeModal();
        navigate('/match');
      } else {
        setError(`Google sign-in error: ${e.data.code}`);
      }
    };
    window.addEventListener('message', onMessage);

    const checkClosed = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
      }
    }, 500);

    function cleanup(): void {
      window.removeEventListener('message', onMessage);
      window.clearInterval(checkClosed);
    }
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {error && (
        <Box sx={{ color: 'error.main', fontSize: '0.875rem' }} role="alert">
          {error}
        </Box>
      )}
      <Button
        variant="outlined"
        size="large"
        onClick={start}
        fullWidth
        startIcon={
          <Box
            component="svg"
            sx={{ width: 20, height: 20 }}
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              fill="#FFC107"
              d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.7 1.1 7.8 3l5.7-5.7A19.9 19.9 0 0 0 24 4a20 20 0 1 0 19.6 16.5z"
            />
            <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8A12 12 0 0 1 24 12c3 0 5.7 1.1 7.8 3l5.7-5.7A19.9 19.9 0 0 0 24 4 20 20 0 0 0 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44a20 20 0 0 0 13.5-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5A19.9 19.9 0 0 0 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.5l6.2 5.2C40.9 36 44 30 44 24c0-1.2-.1-2.4-.4-3.5z" />
          </Box>
        }
      >
        Sign in with Google
      </Button>
    </Box>
  );
}
